import Inherit from '../../core/inherit';
import ShelfController from '../../core/shelf.controller';
import ClientsDelegate from './clients.delegate';
import { User } from '../../data';
import { downloadExcel } from '../../utils/excel';
import { canCreateClientsAndStores } from '../../core/access';

class ClientsController extends Inherit(ShelfController, ClientsDelegate) {
    shouldComponentUpdate(nextProps, nextState) {
        return (
            this.state.clients !== nextState.clients ||
            this.state.users !== nextState.users ||
            this.state.rows !== nextState.rows ||
            this.state.loading !== nextState.loading ||
            this.state.client !== nextState.client ||
            this.state.open !== nextState.open
        );
    }

    async initialize() {
        if (this.isAuthenticated) {
            const [users, clients] = await Promise.all([this.data.fetchUsers(), this.data.fetchClients()]);
            this.formatClientData(clients, users);
        } else this.navigateLogin();
    }

    async formatClientData(clients = [], users = []) {
        const userMap = new Map(users?.map((u) => [u?._id, u]));

        const formattedClients = clients.map((client) => {
            const asesor = userMap.get(client.asesor?.[0]) || {};
            const gerente = userMap.get(client.gerente) || {};

            return {
                _id: client._id ?? '',
                key: client._id ?? '',
                name: client.name ?? '',
                rif: `${client.rif ?? ''}`,
                channel: client.channel ?? '',
                active: client.active ? 'activo' : 'inactivo',
                gerente: gerente.name ?? '',
                gerenteId: client.gerente ?? '',
                asesor: asesor.name ?? '',
                asesorId: client.asesor?.[0] ?? '',
                asesores: client.asesor?.map((id) => userMap.get(id)?.name).filter(Boolean) || [],
                createdAt: client.createdAt ?? '',
                updatedAt: client.updatedAt ?? ''
            };
        });

        // Calculate analytics
        const analytics = this.calculateAnalytics(formattedClients, users);

        this.setState({ clients: formattedClients, rows: formattedClients, users, analytics, loading: false, disabled: false });
    }

    calculateAnalytics(clients, users) {
        const now = new Date();
        const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

        // Filter only active clients first
        const activeClientsData = clients.filter((c) => c.active === 'activo');
        const activeClients = activeClientsData.length;
        const inactiveClients = clients.length - activeClients;

        // Count moderno/tradicional only from active clients
        const modernoClients = activeClientsData.filter((c) => c.channel === 'moderno').length;
        const tradicionalClients = activeClientsData.filter((c) => c.channel === 'tradicional').length;

        // Recent activity (from all clients for historical tracking)
        const recentlyAdded = clients.filter((c) => c.createdAt && new Date(c.createdAt) >= thirtyDaysAgo && c.active === 'activo').length;
        const recentlyUpdated = clients.filter((c) => c.updatedAt && new Date(c.updatedAt) >= sevenDaysAgo && c.active === 'activo').length;

        return {
            totalClients: activeClients, // Only show active clients as total
            activeClients,
            inactiveClients,
            modernoClients, // Only active moderno clients
            tradicionalClients, // Only active tradicional clients
            recentlyAdded,
            recentlyUpdated
        };
    }

    /** Whether the signed-in user may create clients (shows the "Crear Cliente" action). */
    get canCreateClients() {
        return canCreateClientsAndStores(User.getInstance()?.userType);
    }

    /** Exports the currently visible (filtered) rows to Excel. */
    downloadClients() {
        const { rows } = this.state;
        if (!rows) return;
        const titles = ['Nombre', 'RIF', 'Canal', 'Gerente', 'Asesor'];
        const data = rows.map(({ name, rif, channel, gerente, asesor }) => ({
            Nombre: name,
            RIF: rif,
            Canal: channel,
            Gerente: gerente,
            Asesor: asesor
        }));
        downloadExcel(titles, data, 'Clientes');
    }

    async updateClientById(clientId) {
        if (!clientId) return;
        const { onMessage } = this.props;

        this.setState({ loading: true });

        const res = await this.data.updateClientById(clientId, { active: false });
        if (res) {
            onMessage('success', 'Cliente eliminado con éxito!');
            // Re-run the same formatting as the initial load (names, status, analytics).
            const [users, clients] = await Promise.all([this.data.fetchUsers(), this.data.fetchClients()]);
            await this.formatClientData(clients, users);
        } else {
            onMessage('error', 'Error al eliminar el cliente');
            this.setState({ loading: false });
        }
    }

    async showDrawer(id) {
        let client = null;
        if (id && typeof id === 'string') {
            this.setOpen(true);
            client = await this.data.fetchClientById(id);
            this.setState({ client });
        }
        this.setOpen(true);
    }

    async createOrUpdateClient({ name, gerente, asesores, rif, channel, isEdit, gerenteUsers, onCloseDrawer }) {
        const { onMessage } = this.props;
        this.setState({ disabled: true });

        if (!name || !gerente || !asesores || !rif || !channel) {
            onMessage('error', 'Todos los campos son requeridos');
            this.setState({ disabled: false });
            return;
        }

        const cleanedAsesores = Array.isArray(asesores) ? asesores.filter(Boolean) : [];

        const payload = {
            name,
            rif,
            channel,
            asesor: cleanedAsesores,
            gerente: gerente || gerenteUsers?.[0]?._id,
            active: true
        };

        let response = null;
        try {
            response = isEdit ? await this.data.updateClientById(this.state.client?._id ?? '', payload) : await this.data.createClient(payload);
        } catch (error) {
            response = { status: error?.response?.status || 500, message: error?.response?.data?.message || error?.message };
        }

        // Both APIs answer errors as { message, status } (status >= 400), with a 4xx/5xx code.
        if (!response || (typeof response.status === 'number' && response.status >= 400)) {
            onMessage('error', response?.message || (isEdit ? 'Error al editar el cliente' : 'Error al crear el cliente'));
            this.setState({ disabled: false });
            return;
        }

        this.setState({ loading: true, disabled: false });

        const [users, clients] = await Promise.all([this.data.fetchUsers(), this.data.fetchClients()]);
        await this.formatClientData(clients, users);

        onMessage('success', isEdit ? 'Cliente editado correctamente' : 'Cliente creado correctamente');
        onCloseDrawer();
    }
}

export default ClientsController;
